import type { LanguageCode } from '../../texts/languages';

/**
 * The language of the address the router is on, taken from its `/<lang>/` prefix (PDR-0019). One per
 * router: createAppRouter creates it, the router's rewrite (localised-url-rewrite.ts) keeps it up to date
 * and reads it to localise every href, and it is exposed as `router.urlLanguage` for the rest of the app.
 * It is not module state, so routers rendered side by side (tests, a server rendering several requests)
 * don't share a language.
 */
export type UrlLanguage = {
  /**
   * The language of the last language-prefixed address the router parsed, or `undefined` while it has
   * parsed none (a prefix-less address: the Vite dev server, tests with internal paths). A prefix-less
   * address parsed after a prefixed one keeps the language. The Koa server always lands visitors on a
   * prefixed address, so in production it is set before the first render.
   */
  current: () => LanguageCode | undefined;
  /**
   * Calls `listener` synchronously, while the router parses the new address, each time the language
   * changes. Returns the unsubscribe function. With `current` it fits React's useSyncExternalStore.
   */
  subscribe: (listener: () => void) => () => void;
};

/** The writable side, used only by the router's rewrite. */
export type UrlLanguageStore = UrlLanguage & { set: (language: LanguageCode) => void };

export function createUrlLanguage(): UrlLanguageStore {
  let language: LanguageCode | undefined;
  const listeners = new Set<() => void>();

  return {
    current: () => language,
    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    set: (next) => {
      if (next === language) {
        return;
      }
      language = next;
      listeners.forEach((listener) => listener());
    },
  };
}
